/* Promo codes for the cart drawer and cart page (snippets/cart-discount-box.liquid).
   Codes go through /cart/update.js `discount` (a comma-separated list that replaces the cart's
   codes), so every apply/remove sends the full list. A code Shopify reports as not applicable is
   taken straight back off so it never sits silently on the cart. After a change the drawer and
   cart items re-render through the theme's own cartUpdate subscribers, and the cart page footer
   (which they don't cover) is re-fetched here. */
(() => {
  if (customElements.get('cart-discount')) return;

  const norm = (code) => String(code || '').trim().toLowerCase();

  const currentCodes = () => {
    const box = document.querySelector('cart-discount[data-codes]');
    return box ? box.dataset.codes.split(',').map((code) => code.trim()).filter(Boolean) : [];
  };

  const updateCodes = (codes) =>
    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body: JSON.stringify({ discount: codes.join(',') }) }).then(
      (response) => {
        if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
        return response.json();
      }
    );

  // Prefer the cart's discount_codes list; fall back to discount titles on older payloads.
  const isApplied = (cart, code) => {
    if (Array.isArray(cart.discount_codes)) {
      const entry = cart.discount_codes.find((d) => norm(d.code) === norm(code));
      return Boolean(entry && entry.applicable);
    }
    const titles = [
      ...(cart.cart_level_discount_applications || []).map((a) => a.title),
      ...(cart.items || []).flatMap((item) =>
        (item.line_level_discount_allocations || []).map((a) => a.discount_application.title)
      ),
    ];
    return titles.some((title) => norm(title) === norm(code));
  };

  const refreshCart = async () => {
    await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount' });
    const footer = document.getElementById('main-cart-footer');
    if (!footer) return;
    const html = await fetch(`${routes.cart_url}?section_id=${footer.dataset.id}`).then((r) => r.text());
    const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById('main-cart-footer');
    if (fresh) footer.innerHTML = fresh.innerHTML;
  };

  class CartDiscount extends HTMLElement {
    connectedCallback() {
      this.message = this.querySelector('.cdisc__message');
      const form = this.querySelector('.cdisc__form');
      const input = this.querySelector('.cdisc__input');
      if (form && input) {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.apply(input.value);
        });
      }
    }

    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      this.querySelectorAll('button, input').forEach((el) => (el.disabled = busy));
    }

    say(text, isError = false) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.classList.toggle('is-error', isError);
    }

    async apply(raw) {
      const code = String(raw || '').trim();
      if (!code) {
        this.say('Enter a promo code.', true);
        return;
      }
      const codes = currentCodes();
      if (codes.some((c) => norm(c) === norm(code))) {
        this.say(`${code.toUpperCase()} is already applied.`);
        return;
      }

      this.setBusy(true);
      this.say('');
      try {
        const cart = await updateCodes([...codes, code]);
        if (!isApplied(cart, code)) {
          await updateCodes(codes);
          this.setBusy(false);
          this.say(`${code.toUpperCase()} can't be used with the items in your cart.`, true);
          return;
        }
        await refreshCart(); // re-renders this box and the summary with the code applied
      } catch (error) {
        console.error(error);
        this.setBusy(false);
        this.say((window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.', true);
      }
    }
  }

  customElements.define('cart-discount', CartDiscount);

  // Remove links live in the summary rows, outside <cart-discount>, so delegate from the document.
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-remove-code]');
    if (!button) return;
    event.preventDefault();
    button.disabled = true;
    const code = button.dataset.removeCode;
    try {
      await updateCodes(currentCodes().filter((c) => norm(c) !== norm(code)));
      await refreshCart();
    } catch (error) {
      console.error(error);
      button.disabled = false;
    }
  });
})();
